import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search as SearchIcon, Mic, X, Filter, Volume2 } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import LOCard from "@/components/LOCard";
import { TIDS } from "@/constants/testIds";
import { toast } from "sonner";

function useDebounced(value, delay = 350) {
    const [d, setD] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setD(value), delay);
        return () => clearTimeout(id);
    }, [value, delay]);
    return d;
}

export default function SearchPage() {
    const { t, subjectLabel, lang, userId } = useApp();
    const [params, setParams] = useSearchParams();
    const [q, setQ] = useState(params.get("q") || "");
    const [classFilter, setClassFilter] = useState(params.get("class") || "");
    const [subjectFilter, setSubjectFilter] = useState(params.get("subject") || "");
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [listening, setListening] = useState(false);
    const recogRef = useRef(null);
    const debouncedQ = useDebounced(q);

    const [subjects, setSubjects] = useState([]);
    useEffect(() => {
        api.stats().then((s) => setSubjects((s.by_subject || []).map((x) => x.subject)));
    }, []);

    useEffect(() => {
        const next = new URLSearchParams();
        if (q) next.set("q", q);
        if (classFilter) next.set("class", classFilter);
        if (subjectFilter) next.set("subject", subjectFilter);
        setParams(next, { replace: true });
    }, [q, classFilter, subjectFilter, setParams]);

    useEffect(() => {
        setLoading(true);
        const apiParams = { limit: 200 };
        if (debouncedQ) apiParams.q = debouncedQ;
        if (classFilter) apiParams.class_num = parseInt(classFilter, 10);
        if (subjectFilter) apiParams.subject = subjectFilter;
        api.listLO(apiParams).then((r) => {
            setItems(r.items || []);
            setTotal(r.total || 0);
            setLoading(false);
            if (debouncedQ && debouncedQ.length > 1) {
                api.logSearch(debouncedQ, userId).catch(() => {});
            }
        });
    }, [debouncedQ, classFilter, subjectFilter, userId]);

    const startVoice = () => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) {
            toast.error("Voice search not supported on this browser.");
            return;
        }
        const r = new SR();
        r.lang = lang === "od" ? "or-IN" : "en-IN";
        r.interimResults = false;
        r.continuous = false;
        r.onresult = (e) => {
            const text = e.results[0][0].transcript;
            setQ(text);
            setListening(false);
        };
        r.onerror = () => setListening(false);
        r.onend = () => setListening(false);
        r.start();
        recogRef.current = r;
        setListening(true);
    };

    const clearAll = () => { setQ(""); setClassFilter(""); setSubjectFilter(""); };
    const hasFilters = q || classFilter || subjectFilter;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:pb-32">
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-5">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">{t.search}</p>
                <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t.searchLO}</h1>
            </motion.div>

            <div className="glass-strong rounded-3xl p-3 sm:p-4 shadow-sm border border-border/60 mb-5">
                <div className="flex items-center gap-2 bg-background rounded-full pl-4 pr-2 py-1 border border-border">
                    <SearchIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <input
                        data-testid={TIDS.searchInput}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={t.searchPlaceholder}
                        className={`flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground py-2 ${lang === "od" ? "font-odia" : ""}`}
                    />
                    {q && (
                        <button onClick={() => setQ("")} className="h-7 w-7 grid place-items-center rounded-full hover:bg-muted">
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                    <button
                        data-testid={TIDS.searchVoiceBtn}
                        onClick={startVoice}
                        className={`h-8 w-8 rounded-full grid place-items-center active:scale-95 transition-all ${
                            listening ? "bg-destructive text-destructive-foreground animate-pulse" : "bg-primary text-primary-foreground"
                        }`}
                        title={t.voiceSearch}
                    >
                        {listening ? <Volume2 className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                    </button>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-2 mt-3">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground font-semibold">
                        <Filter className="h-3.5 w-3.5" /> {t.filterByClass}:
                    </div>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((c) => (
                        <button
                            key={c}
                            onClick={() => setClassFilter(classFilter === String(c) ? "" : String(c))}
                            data-testid={`${TIDS.filterClass}-${c}`}
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold active:scale-95 transition-all ${
                                classFilter === String(c) ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/70"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground font-semibold">
                        <Filter className="h-3.5 w-3.5" /> {t.filterBySubject}:
                    </div>
                    {subjects.map((s) => (
                        <button
                            key={s}
                            onClick={() => setSubjectFilter(subjectFilter === s ? "" : s)}
                            data-testid={`${TIDS.filterSubject}-${s.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`px-3 py-1 rounded-full text-xs font-semibold active:scale-95 transition-all ${lang === "od" ? "font-odia" : ""} ${
                                subjectFilter === s ? "bg-accent text-accent-foreground" : "bg-muted hover:bg-muted/70"
                            }`}
                        >
                            {subjectLabel(s)}
                        </button>
                    ))}
                    {hasFilters && (
                        <button
                            onClick={clearAll}
                            data-testid={TIDS.clearFilters}
                            className="ml-auto px-3 py-1 rounded-full text-xs font-semibold bg-destructive/10 text-destructive hover:bg-destructive/20 active:scale-95 transition-all"
                        >
                            {t.clearFilters}
                        </button>
                    )}
                </div>
            </div>

            <div className="text-xs text-muted-foreground mb-3 font-medium">
                {loading ? t.loading : `${total} results`}
            </div>

            <div data-testid={TIDS.searchResults} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((e, i) => (
                    <LOCard key={e.id || e.code} entry={e} index={i} />
                ))}
            </div>

            {!loading && items.length === 0 && (
                <div className="text-center text-muted-foreground py-16">{t.noResults}</div>
            )}
        </div>
    );
}
